import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { logger } from "@/lib/logger";
import { createKeyedStore } from "@/lib/storage";
import type { AuthBackend, SignInPayload } from "@/modules/auth/mock-backend";
import { createMockAuthBackend } from "@/modules/auth/mock-backend";
import type { AuthSession, AuthUser } from "@/modules/auth/session-schema";
import { sessionSchema } from "@/modules/auth/session-schema";

type AuthStatus = "idle" | "authenticating" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  error: string | null;
  session: AuthSession | null;
  signIn: (payload: SignInPayload) => Promise<AuthSession>;
  signOut: () => void;
  status: AuthStatus;
  user: AuthUser | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_STORAGE_KEY = "apolo-seed:session";

interface AuthProviderProps {
  backend?: AuthBackend;
  children: ReactNode;
}

export function AuthProvider({
  backend,
  children,
}: AuthProviderProps): JSX.Element {
  const store = useMemo(
    () => createKeyedStore(SESSION_STORAGE_KEY, sessionSchema),
    [],
  );
  const backendRef = useRef<AuthBackend>(backend ?? createMockAuthBackend());

  useEffect(() => {
    if (backend) {
      backendRef.current = backend;
    }
  }, [backend]);

  const [session, setSession] = useState<AuthSession | null>(() => {
    const persisted = store.read();
    if (!persisted) return null;
    if (persisted.expiresAt <= Date.now()) {
      store.clear();
      return null;
    }
    return persisted;
  });
  const [status, setStatus] = useState<AuthStatus>(() =>
    session ? "authenticated" : "unauthenticated",
  );
  const [error, setError] = useState<string | null>(null);

  const signIn = useCallback(
    async (payload: SignInPayload) => {
      setStatus("authenticating");
      setError(null);

      try {
        const nextSession = await backendRef.current.signIn(payload);
        store.write(nextSession);
        setSession(nextSession);
        setStatus("authenticated");
        logger.info("auth.signIn succeeded", { userId: nextSession.user.id });
        return nextSession;
      } catch (caught) {
        const message =
          caught instanceof Error ? caught.message : "Sign-in failed unexpectedly.";
        setError(message);
        setStatus("unauthenticated");
        logger.warn("auth.signIn failed", { message });
        throw caught;
      }
    },
    [store],
  );

  const signOut = useCallback(() => {
    store.clear();
    setSession(null);
    setStatus("unauthenticated");
    setError(null);
  }, [store]);

  const value = useMemo<AuthContextValue>(
    () => ({
      error,
      session,
      signIn,
      signOut,
      status,
      user: session?.user ?? null,
    }),
    [error, session, signIn, signOut, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider.");
  }
  return context;
}
